// src/components/GlobalFileSelector.tsx
// Global data-file selector that lives in the app header. Writes the shared
// activeFileId in DataFilesContext, so the whole platform (dashboard, journal,
// Tajir, …) follows the file chosen here. "All accounts" aggregates every file.

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataFiles } from './DataFilesContext';
import './GlobalFileSelector.css';

export const GlobalFileSelector: React.FC = () => {
  const { standardFiles, activeFileId, setActiveFile, removeFile, loadError } = useDataFiles();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const activeFile = activeFileId ? standardFiles.find(f => f.id === activeFileId) : null;
  const activePlatform = activeFile?.metadata?.platform;
  const totalTrades = standardFiles.reduce((sum, f) => sum + (f.trades?.length || 0), 0);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const choose = useCallback((id: string | null) => {
    setActiveFile(id);
    setOpen(false);
  }, [setActiveFile]);

  // Two-step delete: the first click arms the row, the second confirms. The file
  // goes to the recycle bin and stays restorable for 30 days
  // (Settings → Deleted files), so this is never a destructive dead end.
  const confirmDelete = useCallback(async (id: string) => {
    if (activeFileId === id) setActiveFile(null);
    setPendingDeleteId(null);
    await removeFile(id);
  }, [activeFileId, setActiveFile, removeFile]);

  // An account with no files still gets this control, showing "No file yet".
  //
  // It used to return null, which meant the header silently lost a control on
  // exactly the accounts that needed it most: a new user saw a different header
  // from everyone else, with no import button anywhere, and the product looked
  // like it had lost a feature. A header that changes shape depending on how
  // much data you have reads as a bug even when it is deliberate.
  const isEmpty = standardFiles.length === 0;

  /**
   * A failed read leaves the same empty list as a genuinely new account, and
   * this control then announced "No file yet · Import to start" to somebody
   * whose files exist and are simply unreachable — next to a banner saying the
   * server could not be answered. Say what is actually known instead.
   */
  const loadFailed = Boolean(loadError);
  const title = loadFailed ? 'Files unavailable' : isEmpty ? 'No file yet' : (activeFile ? activeFile.name : 'All accounts');
  const subtitle = loadFailed
    ? 'Cannot reach the server'
    : isEmpty
    ? 'Import to start'
    : activeFile
    ? `${activePlatform ? `${activePlatform} · ` : ''}${activeFile.trades?.length || 0} trades`
    : `${standardFiles.length} files · ${totalTrades} trades`;

  return (
    <div className="gfs" ref={rootRef}>
      <button
        type="button"
        className={`gfs-trigger${open ? ' open' : ''}${isEmpty ? ' is-empty' : ''}`}
        onClick={() => {
          // Do not send someone to the import screen because their files failed
          // to load; there is nothing for them to import.
          if (loadFailed) return;
          if (!isEmpty) setOpen(o => !o);
          else navigate('/data', { state: { openImport: true } });
        }}
        aria-haspopup={isEmpty || loadFailed ? undefined : 'listbox'}
        aria-expanded={isEmpty || loadFailed ? undefined : open}
        title={loadFailed ? 'Your files could not be loaded' : isEmpty ? 'Import your first file' : 'Data file used across the platform'}
      >
        <span className="gfs-ico" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
          </svg>
        </span>
        <span className="gfs-text">
          <span className="gfs-name">{title}</span>
          <span className="gfs-sub">{subtitle}</span>
        </span>
        {!isEmpty && !loadFailed && (
          <svg className="gfs-chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
        )}
      </button>

      <button
        type="button"
        className="gfs-add"
        // Opens the import modal, rather than just dropping the user on the
        // Journal and leaving them to find the Import button themselves. The
        // modal itself has to live in DataTable — its import handler drives
        // that page's file creation, capital prompt and active-file switch —
        // so the route carries the intent and DataTable acts on it.
        onClick={() => navigate('/data', { state: { openImport: true } })}
        title="Import a file"
        aria-label="Import a file"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>

      {open && (
        <div className="gfs-menu" role="listbox">
          <button
            type="button"
            role="option"
            aria-selected={!activeFileId}
            className={`gfs-item${!activeFileId ? ' active' : ''}`}
            onClick={() => choose(null)}
          >
            <span className="gfs-item-main">
              <span className="gfs-item-name">All accounts</span>
              <span className="gfs-item-meta">{standardFiles.length} files · {totalTrades} trades</span>
            </span>
            {!activeFileId && <CheckIcon />}
          </button>

          <div className="gfs-divider" />

          {standardFiles.map(file => {
            const selected = activeFileId === file.id;
            const arming = pendingDeleteId === file.id;
            return (
              <div key={file.id} className={`gfs-row${selected ? ' active' : ''}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`gfs-item${selected ? ' active' : ''}`}
                  onClick={() => choose(file.id)}
                >
                  <span className="gfs-item-main">
                    <span className="gfs-item-name">{file.name}</span>
                    <span className="gfs-item-meta">
                      {arming
                        ? 'Delete this file? It stays recoverable for 30 days.'
                        : `${file.metadata?.platform ? `${file.metadata.platform} · ` : ''}${file.trades?.length || 0} trades`}
                    </span>
                  </span>
                  {selected && !arming && <CheckIcon />}
                </button>

                {arming ? (
                  <span className="gfs-confirm">
                    <button
                      type="button"
                      className="gfs-confirm-yes"
                      title="Move to recycle bin"
                      onClick={(e) => { e.stopPropagation(); confirmDelete(file.id); }}
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      className="gfs-confirm-no"
                      title="Keep this file"
                      onClick={(e) => { e.stopPropagation(); setPendingDeleteId(null); }}
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="gfs-trash"
                    title="Delete file (recoverable for 30 days)"
                    aria-label={`Delete ${file.name}`}
                    onClick={(e) => { e.stopPropagation(); setPendingDeleteId(file.id); }}
                  >
                    <TrashIcon />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TrashIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6M10 11v6M14 11v6" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg className="gfs-check" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export default GlobalFileSelector;
